// Files the client and the agent can send each other during a live chat.
// Keep in step with the Aura backend (live_handoff.ATTACHMENT_TYPES), which checks again.
export const ATTACHMENT_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.webp',
  '.pdf',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv',
];
export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.join(',');
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
// Files waiting in the composer at once; each is sent as its own message, as in Nexus
export const MAX_FILES_PER_SEND = 10;

const extensionOf = (name) => {
  const dot = (name || '').lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
};

// Why this file can't be sent, or null if it can
export const attachmentProblem = (file) => {
  if (!file) return 'No file was chosen.';
  if (!ATTACHMENT_EXTENSIONS.includes(extensionOf(file.name))) {
    return "This file type can't be sent. You can send images, PDFs and Office documents.";
  }
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'That file is larger than 10 MB. Please send a smaller file.';
  return null;
};

export const isImageAttachment = (attachment) =>
  ['.jpg', '.jpeg', '.png', '.webp'].includes(extensionOf(attachment?.file_name));

// "PDF", "DOCX"... for the badge on a document tile
export const extensionLabel = (name) => extensionOf(name).replace('.', '').toUpperCase() || 'FILE';

const KIND_BY_EXTENSION = {
  '.pdf': 'PDF',
  '.doc': 'Word', '.docx': 'Word',
  '.xls': 'Excel', '.xlsx': 'Excel', '.csv': 'CSV',
  '.ppt': 'PowerPoint', '.pptx': 'PowerPoint',
};

export const attachmentKind = (attachment) =>
  KIND_BY_EXTENSION[extensionOf(attachment?.file_name)] || (isImageAttachment(attachment) ? 'Image' : 'File');

// A fresh short-lived link to open a file in this chat. Links aren't stored: each open asks,
// so files in older chats still open. Throws if the file can't be opened right now.
export const fetchAttachmentLink = async (sessionId, attachmentId) => {
  const { REACT_APP_BACKEND_BOT_URL, REACT_APP_AUTH_TOKEN } = process.env;
  const response = await fetch(
    `${REACT_APP_BACKEND_BOT_URL}/chat/attachments/${attachmentId}/link?session_id=${encodeURIComponent(sessionId)}`,
    { headers: { 'Authorization': `${REACT_APP_AUTH_TOKEN}` } }
  );
  if (!response.ok) throw new Error(`link ${response.status}`);
  const { url } = await response.json();
  if (!url) throw new Error('no link');
  return url;
};

export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
